'use client';

import { Button } from '@/components/Button';
import { LocationCtaLink } from '@/components/LocationCtaLink';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { substituteHrefChain } from '@/lib/render/substituteHref';
import type { CtaAction } from '@/types/ctaAction';

interface CtaActionButtonProps {
  action: CtaAction;
  /** Block-level resolved phone (location → domain fallback). */
  fallbackPhone?: string;
  /** Block-level resolved matelso label. */
  fallbackMatelsoPhoneLabel?: string;
  /** Placeholder substitution, e.g. resolves `{location.phone}`. */
  sub: (template: string) => string;
}

/**
 * Renders one `widgets.cta-action`: an explicit `link` wins, otherwise a
 * `tel:` button built from the action's own phone pair.
 *
 * Extracted from `CtaBlockComponent` unchanged so the Accordion's double
 * layout can render the same CTA. Callers with no block-level phone
 * resolution (the Accordion — see the plan's task 2.5) simply pass neither
 * fallback.
 */
export function CtaActionButton({
  action,
  fallbackPhone,
  fallbackMatelsoPhoneLabel,
  sub,
}: CtaActionButtonProps) {
  if (action.type === 'location') {
    return <LocationCtaLink action={action} />;
  }

  const label = action.label ? sub(action.label) : undefined;

  // Link action: explicit link wins
  if (action.link) {
    // An explicit link whose placeholder did not resolve renders nothing — it
    // must not fall through to the phone branch below and dial instead. A
    // `|`-separated chain picks its first resolvable candidate first (SH-524).
    const link = substituteHrefChain(action.link, sub);
    if (!link) {
      return null;
    }
    const variant = action.type === 'secondary' ? 'secondary' : 'primary';
    return (
      <Button
        href={link}
        variant={variant}
        size="lg"
        className="text-sm md:text-lg/8"
        onClick={() =>
          pushCmsEventTracking(action.tracking, { label: label || link })
        }
      >
        {label || link}
      </Button>
    );
  }

  // Phone action: use the action's own (phone, matelsoPhoneLabel) pair as a hard
  // pair (see 70.07-Phone-Number-Resolution) — if the action sets its own phone,
  // its own matelsoPhoneLabel is used (or none), never the block-level label.
  // Only when the action has no phone at all do we fall back to the block-level
  // resolved pair.
  const phone = action.phone ? sub(action.phone) : fallbackPhone;
  if (!phone) {
    return null;
  }
  const matelsoPhoneLabel = action.phone
    ? action.matelsoPhoneLabel
      ? sub(action.matelsoPhoneLabel)
      : undefined
    : fallbackMatelsoPhoneLabel;

  const displayLabel = label || matelsoPhoneLabel || phone;
  const variant = action.type === 'secondary' ? 'secondary' : 'primary';

  return (
    <Button
      href={`tel:${phone.replace(/[^+\d]/g, '')}`}
      variant={variant}
      size="lg"
      onClick={() =>
        pushCmsEventTracking(action.tracking, {
          label: displayLabel,
          phone,
        })
      }
      aria-label={`Anrufen: ${displayLabel}`}
    >
      {displayLabel}
    </Button>
  );
}
